<template>
  <v-text-field
    v-model="currentValue"
    v-bind="$attrs"
    v-on="$listeners"
    :inputmode="inputMode"
    :showAlertIcon="showAlertIcon"
    :hint="hint"
    persistent-hint
  >
    <template v-slot:prepend>
      <slot name="prependMask"></slot>
    </template>

    <template v-slot:append>
      <slot name="appendMask"></slot>
      <v-icon v-if="showAlertIcon" color="warning">mdi-alert-circle</v-icon>
    </template>

    <template v-slot:append-inner>
      
    </template>
  </v-text-field>
</template>

<script>
import Cleave from 'cleave.js';
//documentation -> https://github.com/nosir/cleave.js/blob/master/doc/options.md

// this component is a generic text-field with mask
// it inherits the attrs from the parent, such as class, title, label, rules...
// for each new mask, an object has to be declared with the options and passed to the Cleave object (needs to add a new if condition with a new 'key')
// the inputData is copied to currentValue and it emits back to the parent two events
//   input-masked-data => the value with mask
//   input-raw-data    => the raw value, without mask

export default {
  props: {
    showAlertIcon:{
      type: Boolean,
      default: false,
    },
    inputData: {
      type: [String, Number],
    },
    maskType: {
      type: String,
      required: true
    },
    hint: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      currentValue: null,
      inputMode:"decimal",
      //simple data mask= "30/10/2019"
      maskOptionsDate: {
        date: true,
        datePattern: ['d', 'm', 'Y'],
      },
      maskOptionsDateRange: {
        numericOnly: true,
        blocks: [2, 2, 4, 2, 2, 4],
        delimiters: ['/', '/', ' ~ ', '/', '/']
      },
      maskOptionsMonth: {
        date: true,
        datePattern: ['m', 'Y'],
      },
      maskOptionsTime: {
        time: true,
        timePattern: ['h', 'm'],
      },
      maskOptionsDateTime: {
        numericOnly: true,
        blocks: [2, 2, 4, 2, 2],
        delimiters: ['/', '/', ' ', ':']
      },
      maskOptionsCEP: {
        numericOnly: true,
        blocks: [5, 3],
        delimiters: ['-']
      },
      maskOptionsCPF: {
        numericOnly: true,
        blocks: [3, 3, 3, 2],
        delimiters: ['.', '.', '-']
      },
      maskOptionsCNPJ: {
        numericOnly: false,
        blocks: [2, 3, 3, 4, 2],
        delimiters: ['.', '.', '/', '-']
      },
      maskOptionsRG: {
        blocks: [10],
        delimiters: ['']
      },
      maskOptionsPhone: {
        phone: true,
        numericOnly: true,
        phoneRegionCode: 'BR'
      },
      maskOptionsPlate: {
        blocks: [3, 4],
        delimiters: [' '],
        uppercase: true
      },
      maskOptionsNumberOnly: {
        numericOnly: true,
        blocks: [10]
      },
      maskOptionsInteger: {
        numeral: true,
        numeralPositiveOnly: true,
        numeralDecimalScale: 0,
        numeralDecimalMark: ',',
        delimiter: '.'
      },
      maskOptionsYear: {
        numeral: true,
        numeralPositiveOnly: true,
        numeralDecimalScale: 0,
        numeralIntegerScale: 4,
        numeralDecimalMark: ',',
        delimiter: ''
      },
      //default currency mask = "12.123,56"
      maskOptionsCurrency: {
        numeral: true,
        numeralDecimalScale: 2,
        numeralDecimalMark: ',',
        delimiter: '.',
        numeralPositiveOnly: true
      },
      maskOptionsCurrencyNegative: {
        numeral: true,
        numeralDecimalScale: 2,
        numeralDecimalMark: ',',
        delimiter: '.',
        numeralPositiveOnly: false
      },
      maskOptionsCurrency_10d: {
        numeral: true,
        // numeralIntegerScale: 6,
        numeralDecimalScale: 10,
        numeralDecimalMark: ',',
        delimiter: '.',
        numeralPositiveOnly: true
      },
      maskOptionsCurrency3v2_4: {
        numeral: true,
        numeralIntegerScale: 3,
        numeralDecimalScale: 4,
        numeralDecimalMark: ',',
        delimiter: '.',
        numeralPositiveOnly: true
      },
      maskOptionsCurrency13v2: {
        numeral: true,
        numeralIntegerScale: 13,
        numeralDecimalScale: 2,
        numeralDecimalMark: ',',
        delimiter: '.',
        numeralPositiveOnly: true
      },
      maskOptionsCurrency11v0_4: {
        numeral: true,
        numeralIntegerScale: 11,
        numeralDecimalScale: 4,
        numeralDecimalMark: ',',
        delimiter: '.',
        numeralPositiveOnly: true
      },
      maskOptionsCurrency12v0_4: {
        numeral: true,
        numeralIntegerScale: 12,
        numeralDecimalScale: 4,
        numeralDecimalMark: ',',
        delimiter: '.',
        numeralPositiveOnly: true
      }
    };
  },
  mounted() { 
    var maskOptions = null;
    if (this.maskType == "date") {
      maskOptions = this.maskOptionsDate;
      this.inputMode = "numeric";
    } else if (this.maskType == "date_range") {
      maskOptions = this.maskOptionsDateRange;
      this.inputMode = "numeric";
    } else if (this.maskType == "month") {
      maskOptions = this.maskOptionsMonth;
      this.inputMode = "numeric";
    } else if (this.maskType == "time") {
      maskOptions = this.maskOptionsTime;
      this.inputMode = "numeric";
    } else if (this.maskType == "datetime") {
      maskOptions = this.maskOptionsDateTime;
      this.inputMode = "numeric";
    } else if (this.maskType == "cep") {
      maskOptions = this.maskOptionsCEP;
      this.inputMode = "numeric";
    } else if (this.maskType == "cpf") {
      maskOptions = this.maskOptionsCPF;
      this.inputMode = "numeric";
    } else if (this.maskType == "cnpj") {
      maskOptions = this.maskOptionsCNPJ;
      this.inputMode = "numeric";
    } else if (this.maskType == "rg") {
      maskOptions = this.maskOptionsRG;
      this.inputMode = "numeric";
    } else if (this.maskType == "phone") {
      maskOptions = this.maskOptionsPhone;
      this.inputMode = "tel";
    } else if (this.maskType == "plate") {
      maskOptions = this.maskOptionsPlate;
      this.inputMode = "text";
    } else if (this.maskType == "integer") {
      maskOptions = this.maskOptionsInteger;
      this.inputMode = "numeric";
    } else if (this.maskType == "number_only") {
      maskOptions = this.maskOptionsNumberOnly;
      this.inputMode = "numeric";
    } else if (this.maskType == "year") {
      maskOptions = this.maskOptionsYear;
      this.inputMode = "numeric";
    } else if (this.maskType == "currency") {
      maskOptions = this.maskOptionsCurrency;
      this.inputMode = "decimal";
    } else if (this.maskType == "currency_negative") {
      maskOptions = this.maskOptionsCurrencyNegative;
      this.inputMode = "text";
    } else if (this.maskType == "currency_10d") {
      maskOptions = this.maskOptionsCurrency_10d;
      this.inputMode = "decimal";
    } else if (this.maskType == "3v2-4") {
      maskOptions = this.maskOptionsCurrency3v2_4;
      this.inputMode = "decimal";
    } else if (this.maskType == "13v2") {
      maskOptions = this.maskOptionsCurrency13v2;
      this.inputMode = "decimal";
    } else if (this.maskType == "11v0-4") {
      maskOptions = this.maskOptionsCurrency11v0_4;
      this.inputMode = "decimal";
    } else if (this.maskType == "12v0-4") {
      maskOptions = this.maskOptionsCurrency12v0_4;
      this.inputMode = "decimal";
    }
    maskOptions.onValueChanged = this.onValueChanged.bind(this)
    this.cleave = new Cleave(this.$el.querySelector('input'), maskOptions);

    // console.log("1 - this.currentValue, this.inputData", this.currentValue, this.inputData)

    // if (this.inputData && this.inputData.toString().indexOf('e')) {
    //   this.currentValue = this.scientificToDecimal(this.inputData.toString());
    // }

    // console.log("2 - this.currentValue, this.inputData", this.currentValue, this.inputData)

    this.setRawData();

    // console.log("3 - this.currentValue, this.inputData", this.currentValue, this.inputData)
    // this.currentValue = this.inputData ? this.inputData.toString().replace(".", ",") : null;
    if (this.inputData === 0) {
      this.currentValue = 0;
    }
    
  },
  methods: {
    onValueChanged({ target }) {
      this.currentValue = target.value;
      // console.log("mask input emit", target.value, target.rawValue, this.inputData);
      if ((this.inputData != target.rawValue && this.cleave && this.cleave.properties && this.cleave.properties.numeral) ||
          (this.inputData != target.value && this.cleave && this.cleave.properties && this.cleave.properties.date) ||
          (this.inputData != target.rawValue && this.cleave && this.cleave.properties && this.cleave.properties.time) || this.maskType == "datetime") {
        this.$emit('input-masked-data', target.value);
        // this.$emit('input-raw-data', target.rawValue);
        if (this.cleave && this.cleave.properties && this.cleave.properties.numeral) {
          this.$emit('update:inputData', parseFloat(target.rawValue));
        } else if (this.cleave && this.cleave.properties && this.cleave.properties.time) {
          this.$emit('update:inputData', target.value);
        } else {
          this.$emit('update:inputData', target.rawValue);
        }
      } else if (this.cleave && (!this.cleave.properties.numeral && !this.cleave.properties.date)) {
        this.$emit('update:inputData', target.rawValue);
      }
      // this.$nextTick(() => {
      // });
    },
    setRawData: function() {
      if (this.inputData) {
        if (this.inputData.toString().indexOf('e')) {
          this.cleave.setRawValue(this.scientificToDecimal(this.inputData.toString()));
        } else {
          this.cleave.setRawValue(this.inputData);
        }
      }
    },
    scientificToDecimal: function(number) {
      let numberHasSign = number.startsWith("-") || number.startsWith("+");
      let sign = numberHasSign ? number[0] : "";
      number = numberHasSign ? number.replace(sign, "") : number;

      //if the number is in scientific notation remove it
      if (/\d+\.?\d*e[\+\-]*\d+/i.test(number)) {
        let zero = '0';
        let parts = String(number).toLowerCase().split('e'); //split into coeff and exponent
        let e = parts.pop();//store the exponential part
        let l = Math.abs(e); //get the number of zeros
        let sign = e / l;
        let coeff_array = parts[0].split('.');

        if (sign === -1) {
          coeff_array[0] = Math.abs(coeff_array[0]);
          number = zero + '.' + new Array(l).join(zero) + coeff_array.join('');
        } else {
          let dec = coeff_array[1];
          if (dec) l = l - dec.length;
          number = coeff_array.join('') + new Array(l + 1).join(zero);
        }
      }

      return `${sign}${number}`;
    }
  },
};
</script>
<style>
/* .v-input__icon--append .v-icon { 
    color: orange;
} */
</style>